import React from "react";
import { Link } from "react-router-dom";
import {
  Award,
  Cloud,
  MessageCircle,
  HeartHandshake,
  FileCheck2,
  Users2,
  Handshake,
} from "lucide-react";
import { Reveal } from "../components/site/Layout";
import { aboutContent, stats, whyUs } from "../data/mock";

const whyUsIcons = { Award, MessageCircle, Cloud, HeartHandshake };

const workflow = [
  {
    icon: Handshake,
    title: "1. Discuție inițială",
    text: "O discuție inițială, telefonic sau online, în care clarificăm volumul de documente, numărul de angajați și prioritățile actuale. La final, propunem un mod de lucru concret, adaptat situației.",
  },
  {
    icon: Cloud,
    title: "2. Transmiterea documentelor",
    text: "Se configurează un folder partajat pe drive, în care documentele sunt încărcate pe măsură ce apar — poze, scan-uri sau PDF-uri, în funcție de ce este mai practic.",
  },
  {
    icon: FileCheck2,
    title: "3. Operare lunară",
    text: "Documentele sunt înregistrate, statele de plată întocmite, REVISAL-ul actualizat, iar declarațiile depuse la termen. Lunar, transmitem o situație clară a intrărilor, ieșirilor și obligațiilor viitoare.",
  },
  {
    icon: Users2,
    title: "4. Suport curent",
    text: "Pentru întrebări punctuale sau situații care necesită o decizie rapidă — o angajare nouă, un decont, o solicitare de la ANAF sau ITM — comunicarea rămâne directă, la telefon sau pe WhatsApp.",
  },
];

const About = () => {
  return (
    <>
      <section className="page-hero">
        <div className="site-container">
          <h1>Despre noi</h1>
          <p>
            Peste 30 de ani de activitate în contabilitate primară și
            resurse umane. Câteva cuvinte despre modul nostru de lucru.
          </p>
        </div>
      </section>

      {/* Story */}
      <section className="section">
        <div className="site-container">
          <div className="about-grid">
            <Reveal>
              <div className="about-image">
                <img
                  src={aboutContent.image}
                  alt="Colegii lucrând împreună la documente"
                  loading="lazy"
                />
              </div>
            </Reveal>
            <Reveal delay={150}>
              <h2 className="section-title">Povestea noastră</h2>
              <p className="section-lead" style={{ marginBottom: 20 }}>
                Accent Prestări Servicii a fost înființată pentru a
                oferi firmelor mici și mijlocii un partener serios pe
                partea de documente și de personal — nu servicii
                impersonale, ci o colaborare stabilă, bazată pe rezultate.
                Ne-am concentrat de la început pe două direcții pe care
                le stăpânim bine: <strong>contabilitatea primară</strong>{" "}
                și <strong>resursele umane</strong>.
              </p>
              <p className="section-lead" style={{ marginBottom: 20 }}>
                Peste 30 de ani de experiență în domeniu ne-au învățat un
                lucru esențial: cifrele nu sunt niciodată doar cifre.
                Sunt indicatori despre modul în care funcționează o
                afacere — dacă direcția este corectă, unde se cheltuiește,
                când este momentul unei angajări. De aceea, pe lângă
                partea tehnică, oferim și{" "}
                <strong>consultanță de management</strong> asupra
                acestor semnale. Decizia rămâne, întotdeauna, a clientului.
              </p>
              <p className="section-lead" style={{ marginBottom: 20 }}>
                Colaborarea funcționează <strong>100% digital</strong>.
                Documentele se transmit pe drive — poze, scan-uri sau
                PDF-uri — fără drumuri suplimentare și fără dosare fizice.
                Pentru întrebările curente sau pentru situațiile care apar
                între închiderile lunare, comunicarea se face direct, la
                telefon sau pe WhatsApp, fără intermediari.
              </p>
              <p className="section-lead">
                Principiile pe care lucrăm sunt simple: termene respectate,
                comunicare clară și transparentă, fără promisiuni pe care
                nu le putem susține.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="stats-band">
        <div className="site-container">
          <div className="stats-grid">
            {stats.map((s, i) => (
              <Reveal key={s.label} delay={i * 120}>
                <div className="stat-value">{s.value}</div>
                <div className="stat-label">{s.label}</div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Why us */}
      <section className="section">
        <div className="site-container">
          <Reveal>
            <h2
              className="section-title"
              style={{ textAlign: "center", marginBottom: 64 }}
            >
              Ce ne recomandă
            </h2>
          </Reveal>
          <div className="whyus-grid">
            {whyUs.map((w, i) => {
              const Icon = whyUsIcons[w.icon];
              return (
                <Reveal key={w.title} delay={i * 120}>
                  <div className="whyus-item">
                    <div className="whyus-icon">
                      <Icon size={26} />
                    </div>
                    <h3 className="whyus-title">{w.title}</h3>
                    <p className="whyus-text">{w.text}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* How we work */}
      <section className="section section-soft-teal">
        <div className="site-container">
          <Reveal>
            <h2
              className="section-title"
              style={{ textAlign: "center", marginBottom: 16 }}
            >
              Cum lucrăm împreună
            </h2>
            <p
              className="section-lead"
              style={{ textAlign: "center", margin: "0 auto 56px" }}
            >
              Un proces simplu, în patru etape — de la prima discuție,
              până la operarea lunară de rutină.
            </p>
          </Reveal>
          <div className="whyus-grid">
            {workflow.map((w, i) => {
              const Icon = w.icon;
              return (
                <Reveal key={w.title} delay={i * 120}>
                  <div className="whyus-item">
                    <div className="whyus-icon">
                      <Icon size={26} />
                    </div>
                    <h3 className="whyus-title">{w.title}</h3>
                    <p className="whyus-text">{w.text}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section">
        <div className="site-container">
          <Reveal>
            <div className="cta-band">
              <h2>Programează o discuție inițială</h2>
              <p>
                O convorbire scurtă, fără costuri și fără angajament, în
                care evaluăm împreună situația actuală și modul în care
                putem contribui.
              </p>
              <Link to="/contact" className="btn-accent">
                Contactează-ne
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
};

export default About;
